import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  TextInput,
  Platform,
  Linking,
  Alert,
  useWindowDimensions,
} from "react-native";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { FASTAPI_BASE_URL, PDF_BASE_URL } from "./config";

/**
 * TeacherTestReport (Attempt-scoped grading)
 * - Left: students + attempts
 * - Right: selected attempt details + per-question grading
 * - Prefills score (override > saved > auto-suggest for MCQ/TF/FITB)
 * - Warns on unsaved edits when switching attempts
 */
const TeacherTestReport = ({ testId = null, isDarkMode, styles }) => {
  const [results, setResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);

  // Responsive window sizing
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1024;
  
  // Responsive panel width: full on mobile, 40% on tablet, 340px on desktop
  const leftPanelWidth = isMobile ? "100%" : (isTablet ? "40%" : 340);

  // Attempt selection
  const [selectedAttemptId, setSelectedAttemptId] = useState(null);
  const [currentAttemptId, setCurrentAttemptId] = useState(null);

  // { [attemptId]: { [test_question_id]: { teacher_score, teacher_comment } } }
  const [gradingEdits, setGradingEdits] = useState({});
  // { [attemptId]: true/false } -> unsaved/dirtiness flag
  const [unsaved, setUnsaved] = useState({});

  const auth = useAuth();
  if (!auth) {
    console.error("⚠️ AuthContext is missing! Ensure AuthProvider wraps the app.");
    return <Text>⚠️ Authentication error. Please login again.</Text>;
  }
  const { user } = auth;
  const user_id = user?.email || "unknown_teacher";

  // ---------------------------
  // Helpers (state + utilities)
  // ---------------------------
  const getEditsFor = (attemptId) => gradingEdits[attemptId] || {};
  const markDirty = (attemptId, dirty = true) =>
    setUnsaved((prev) => ({ ...prev, [attemptId]: dirty }));

  const updateGrading = (qid, data) => {
    if (!currentAttemptId) return;
    setGradingEdits((prev) => {
      const perAttempt = prev[currentAttemptId] || {};
      const prevQ = perAttempt[qid] || {};
      return {
        ...prev,
        [currentAttemptId]: { ...perAttempt, [qid]: { ...prevQ, ...data } },
      };
    });
    markDirty(currentAttemptId, true);
  };

  // Confirm before discarding unsaved edits when switching attempts
  const confirmDiscard = async () => {
    if (!unsaved[currentAttemptId]) return true;
    if (Platform.OS === "web") {
      return window.confirm("You have unsaved grading changes for this attempt. Discard them?");
    }
    return new Promise((resolve) => {
      Alert.alert(
        "Unsaved changes",
        "You have unsaved grading changes for this attempt. Discard them?",
        [
          { text: "Cancel", style: "cancel", onPress: () => resolve(false) },
          { text: "Discard", style: "destructive", onPress: () => resolve(true) },
        ]
      );
    });
  };

  // Fetch results for a test
  useEffect(() => {
    const fetchResults = async () => {
      if (!testId) return;
      setLoadingResults(true);
      try {
        const res = await axios.get(`${FASTAPI_BASE_URL}/tests/${testId}/results`);
        setResults(res.data || []);
      } catch (error) {
        console.error("Error fetching test results:", error);
      } finally {
        setLoadingResults(false);
      }
    };
    fetchResults();
  }, [testId]);

  // Group by student and sort attempts by completed_at desc (newest first)
  const grouped = useMemo(() => {
    const byStudent = new Map();
    for (const r of results) {
      const email = r.student_email || "unknown_student";
      if (!byStudent.has(email)) byStudent.set(email, []);
      byStudent.get(email).push(r);
    }
    const arr = Array.from(byStudent.entries()).map(([email, attempts]) => {
      attempts.sort((a, b) => {
        const ta = a.completed_at ? new Date(a.completed_at).getTime() : 0;
        const tb = b.completed_at ? new Date(b.completed_at).getTime() : 0;
        return tb - ta;
      });
      attempts.forEach((a, idx) => (a.__attemptNo = idx + 1)); // 1 = latest
      return { email, attempts };
    });
    arr.sort((a, b) => a.email.localeCompare(b.email));
    return arr;
  }, [results]);

  // Auto-select first available attempt
  useEffect(() => {
    if (!selectedAttemptId && grouped.length > 0 && grouped[0].attempts.length > 0) {
      const firstId = grouped[0].attempts[0].id;
      setSelectedAttemptId(firstId);
      setCurrentAttemptId(firstId);
    }
  }, [grouped, selectedAttemptId]);

  const selectedAttempt = useMemo(
    () => results.find((r) => r.id === selectedAttemptId) || null,
    [results, selectedAttemptId]
  );

  const handleSelectAttempt = async (attempt) => {
    if (currentAttemptId && currentAttemptId !== attempt.id) {
      const ok = await confirmDiscard();
      if (!ok) return;
    }
    setSelectedAttemptId(attempt.id);
    setCurrentAttemptId(attempt.id);
  };

  // ---------------------------
  // Display / parse helpers
  // ---------------------------
  const fmtDate = (d) => {
    if (!d) return "N/A";
    try {
      return new Date(d).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return String(d);
    }
  };

  const optionToText = (optObj) => {
    if (!optObj) return "";
    if (Array.isArray(optObj.segments)) {
      const text = optObj.segments
        .filter((seg) => seg && seg.type === "Text" && typeof seg.text === "string")
        .map((seg) => seg.text)
        .join(" ")
        .trim();
      if (text) return text;
    }
    return optObj.text || "";
  };

  const parseJSONSafe = (raw, fallback = null) => {
    try {
      return raw ? JSON.parse(raw) : fallback;
    } catch {
      return fallback;
    }
  };

  const getMaxScore = (entry) => {
    const m = entry?.max_score;
    if (m == null) return 1;
    const n = Number(m);
    return Number.isFinite(n) ? n : 1;
  };

  // Returns { suggested, isAutoGradable }
  const autoSuggestScore = (entry) => {
    const maxScore = getMaxScore(entry);
    const parsedOptions = parseJSONSafe(entry.options, null);
    const rawStudent = parseJSONSafe(entry.student_answer, entry.student_answer ?? null);
    const qtype = entry.question_type;

    // canonical correct
    let correct = null;
    if (qtype === "2" || qtype === "4" || qtype === 2 || qtype === 4) {
      correct = parsedOptions?.correct_answer ?? null;
    } else if (qtype === "1" || qtype === 1) {
      correct = parsedOptions?.correct_answer ?? null;
    }

    // compare logic
    if (qtype === "1" || qtype === 1) {
      if (rawStudent == null || correct == null) return { suggested: undefined, isAutoGradable: true };
      const ok =
        String(rawStudent).trim().toLowerCase() === String(correct).trim().toLowerCase();
      return { suggested: ok ? maxScore : 0, isAutoGradable: true };
    }
    if (qtype === "2" || qtype === 2) {
      // MCQ "optionN" or "N"
      let stuNum = null;
      if (typeof rawStudent === "string" && /^option\d+$/i.test(rawStudent)) {
        stuNum = parseInt(rawStudent.replace(/^\D+/g, ""), 10);
      } else if (typeof rawStudent === "string" && /^\d+$/.test(rawStudent)) {
        stuNum = parseInt(rawStudent, 10);
      }
      let corrNum = null;
      if (typeof correct === "string" && /^\d+$/.test(correct)) {
        corrNum = parseInt(correct, 10);
      } else if (typeof correct === "string" && /^option\d+$/i.test(correct)) {
        corrNum = parseInt(correct.replace(/^\D+/g, ""), 10);
      }
      if (Number.isFinite(stuNum) && Number.isFinite(corrNum)) {
        return { suggested: stuNum === corrNum ? maxScore : 0, isAutoGradable: true };
      }
      return { suggested: undefined, isAutoGradable: true };
    }
    if (qtype === "4" || qtype === 4) {
      if (rawStudent == null || correct == null) return { suggested: undefined, isAutoGradable: true };
      const ok =
        String(rawStudent).trim().toLowerCase() === String(correct).trim().toLowerCase();
      return { suggested: ok ? maxScore : 0, isAutoGradable: true };
    }
    // essays / word-pairs etc.
    return { suggested: undefined, isAutoGradable: false };
  };

  const buildAnswerDisplay = (entry) => {
    const parsedOptions = parseJSONSafe(entry.options, null);
    const rawStudent = parseJSONSafe(entry.student_answer, entry.student_answer ?? null);

    // Student answer -> "(Option N) Text" or raw
    let studentDisplay = "No answer";
    if (rawStudent == null) {
      studentDisplay = "No answer";
    } else if (typeof rawStudent === "string" && /^option\d+$/i.test(rawStudent) && parsedOptions) {
      const key = rawStudent;
      const obj = parsedOptions[key];
      const txt = optionToText(obj) || key;
      const m = key.match(/^option(\d+)$/i);
      studentDisplay = m?.[1] ? `(Option ${m[1]}) ${txt}` : txt;
    } else if (Array.isArray(rawStudent)) {
      studentDisplay = rawStudent.join(", ");
    } else {
      studentDisplay = String(rawStudent);
    }

    // Correct answer -> "(Option N) Text" or raw
    let correctDisplay = "[N/A]";
    const qtype = entry.question_type;
    if (qtype === "2" || qtype === 2 || qtype === "4" || qtype === 4) {
      const corr = parsedOptions?.correct_answer;
      if (corr != null) {
        if (typeof corr === "string" && /^\d+$/.test(corr) && parsedOptions) {
          const corrKey = "option" + corr;
          const obj = parsedOptions[corrKey];
          const txt = optionToText(obj) || corrKey;
          correctDisplay = `(Option ${corr}) ${txt}`;
        } else if (typeof corr === "string" && /^option\d+$/i.test(corr) && parsedOptions) {
          const obj = parsedOptions[corr];
          const txt = optionToText(obj) || corr;
          const m = corr.match(/^option(\d+)$/i);
          correctDisplay = m?.[1] ? `(Option ${m[1]}) ${txt}` : txt;
        } else {
          correctDisplay = String(corr);
        }
      }
    } else if (qtype === "1" || qtype === 1) {
      const corr = parsedOptions?.correct_answer;
      if (corr != null) correctDisplay = String(corr);
    } else if (qtype === "10" || qtype === 10 || qtype === "11" || qtype === 11) {
      correctDisplay = "[Teacher graded]";
    } else if (qtype === "9" || qtype === 9) {
      correctDisplay = "[See pairs]";
    }

    return { studentDisplay, correctDisplay };
  };

  // ---------------------------
  // Right pane: selected attempt
  // ---------------------------
  const renderAttemptDetail = (attempt) => {
    if (!attempt) {
      return (
        <View style={{ padding: 12 }}>
          <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
            Select a student attempt to view details.
          </Text>
        </View>
      );
    }

    const editsForThisAttempt = getEditsFor(currentAttemptId);

    return (
      <View style={{ padding: 12 }}>
        {/* Summary header */}
        <View
          style={{
            backgroundColor: isDarkMode ? "#2b2b2b" : "#f5f7fb",
            padding: 12,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: isDarkMode ? "#3a3a3a" : "#e1e6ef",
            marginBottom: 12,
          }}
        >
          <Text style={{ color: isDarkMode ? "#fff" : "#000", fontWeight: "700", fontSize: 16 }}>
            {attempt.student_email}
          </Text>
          <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
            Attempt #{attempt.__attemptNo} • Submitted: {fmtDate(attempt.completed_at)}
          </Text>
          <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
            Score: {attempt.score} / {attempt.total_score} (
            {Math.round((attempt.score / Math.max(1, attempt.total_score)) * 100)}%)
          </Text>
          <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>Time Taken: {attempt.time_taken} sec</Text>

   
          {/* PDF links + Submit Grades (summary row) */}
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12, marginTop: 8 }}>
            {attempt.pdf_path ? (
              <TouchableOpacity
                onPress={() => {
                  try {
                    const url = `${PDF_BASE_URL}/${attempt.pdf_path}`;
                    if (Platform.OS === "web") window.open(url, "_blank");
                    else Linking.openURL(url);
                  } catch (err) {
                    console.error("PDF open error", err);
                    Alert.alert("Error", "Could not open PDF file.");
                  }
                }}
                style={{ paddingVertical: 6, paddingHorizontal: 10, backgroundColor: "#111827", borderRadius: 6 }}
              >
                <Text style={{ color: "#fff" }}>📄 View Submitted PDF</Text>
              </TouchableOpacity>
            ) : null}

            {attempt.graded_pdf_path ? (
              <TouchableOpacity
                onPress={() => {
                  const url = `${PDF_BASE_URL}/${attempt.graded_pdf_path}`;
                  if (Platform.OS === "web") window.open(url, "_blank");
                  else Linking.openURL(url);
                }}
                style={{ paddingVertical: 6, paddingHorizontal: 10, backgroundColor: "#14532d", borderRadius: 6 }}
              >
                <Text style={{ color: "#fff" }}>📄 View Graded PDF</Text>
              </TouchableOpacity>
            ) : null}

            {/* 💾 Submit Grades button */}
            <TouchableOpacity
              onPress={async () => {
                const attemptId = attempt.id;

                // Pull edits for THIS attempt (from the per-attempt map)
                const edits = getEditsFor(attemptId); // ← from the component state we set up earlier

                // Build graded_answers using: override (edits) > existing DB score > auto-suggest > 0
                const graded_answers = (attempt.answers || []).map((entry) => {
                  const qid = entry.test_question_id;
                  const maxScore = getMaxScore(entry);                // helper from the full file I gave you
                  const { suggested } = autoSuggestScore(entry);      // helper from the full file I gave you

                  const overrideScore = edits[qid]?.teacher_score;
                  const overrideComment = edits[qid]?.teacher_comment;

                  const existingScore =
                    entry.teacher_score !== null && entry.teacher_score !== undefined
                      ? Number(entry.teacher_score)
                      : undefined;

                  let finalScore =
                    overrideScore !== undefined ? overrideScore
                    : existingScore !== undefined ? existingScore
                    : suggested !== undefined ? suggested
                    : 0;

                  if (!Number.isFinite(finalScore)) finalScore = 0;
                  finalScore = Math.max(0, Math.min(maxScore, finalScore));

                  const finalComment = overrideComment ?? entry.teacher_comment ?? "";

                  return {
                    test_question_id: parseInt(qid, 10),
                    teacher_score: finalScore,
                    teacher_comment: finalComment,
                  };
                });

                const payload = {
                  student_test_id: attemptId,
                  teacher_email: user_id,
                  graded_answers,
                };

                try {
                  await axios.post(`${FASTAPI_BASE_URL}/teacher/grade-student-test`, payload);
                  Alert.alert("✅ Success", "Grades saved.");
                  markDirty(attemptId, false); // optional: clear "unsaved" flag for this attempt
                  // Optionally clear edits for this attempt:
                  // setGradingEdits((prev) => ({ ...prev, [attemptId]: {} }));
                } catch (err) {
                  console.error("❌ Grading error:", err);
                  Alert.alert("Error", "Failed to save grades.");
                }
              }}
              style={{ paddingVertical: 6, paddingHorizontal: 12, backgroundColor: "#2563eb", borderRadius: 6 }}
            >
              <Text style={{ color: "#fff", fontWeight: "600" }}>💾 Submit Grades</Text>
            </TouchableOpacity>
          </View>

        </View>

        {/* Questions list for this attempt */}
        <ScrollView
          style={{ maxHeight: 520, borderWidth: 1, borderColor: "#ddd", borderRadius: 8 }}
          contentContainerStyle={{ padding: 12 }}
        >
          {(attempt.answers || []).map((entry, idxQuestion) => {
            const qid = entry.test_question_id;
            const displayNumber = idxQuestion + 1;

            // Student-facing question stem (from question_text JSON)
            const parsedQuestionPayload = parseJSONSafe(entry.question_text, {});
            const questionStemText = parsedQuestionPayload?.text || "[Question text missing]";

            // Displays for student/correct
            const { studentDisplay, correctDisplay } = buildAnswerDisplay(entry);

            // Scoring prefs: teacher override > saved > auto-suggest
            const maxScoreNum = getMaxScore(entry);
            const { suggested } = autoSuggestScore(entry);

            const edit = editsForThisAttempt[qid] || {};
            const displayScore =
              edit.teacher_score !== undefined && edit.teacher_score !== null
                ? String(edit.teacher_score)
                : entry.teacher_score !== null && entry.teacher_score !== undefined
                ? String(entry.teacher_score)
                : suggested !== undefined
                ? String(suggested)
                : "";

            const displayComment = edit.teacher_comment ?? entry.teacher_comment ?? "";

            return (
              <View
                key={qid}
                style={{
                  paddingVertical: 10,
                  borderBottomWidth: 1,
                  borderColor: isDarkMode ? "#333" : "#eee",
                }}
              >
                <Text style={{ color: isDarkMode ? "#fff" : "#000", fontWeight: "600" }}>
                  Q{displayNumber}: {questionStemText}
                </Text>

                <Text style={{ color: isDarkMode ? "#fff" : "#000", fontStyle: "italic", marginBottom: 4 }}>
                  Points possible: {Number.isFinite(maxScoreNum) ? maxScoreNum : "?"}
                </Text>

                <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
                  Student Answer: {studentDisplay}
                </Text>
                <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
                  Correct Answer: {correctDisplay}
                </Text>

                <TextInput
                  placeholder="Teacher Comment"
                  value={displayComment}
                  onChangeText={(text) => updateGrading(qid, { teacher_comment: text })}
                  style={{
                    borderWidth: 1,
                    borderColor: "#aaa",
                    padding: 5,
                    marginTop: 6,
                    marginBottom: 6,
                    backgroundColor: "#fff",
                  }}
                />

                <Text
                  style={{
                    color: isDarkMode ? "#fff" : "#000",
                    marginTop: 4,
                    marginBottom: 4,
                    fontWeight: "500",
                  }}
                >
                  Score (out of {Number.isFinite(maxScoreNum) ? maxScoreNum : "?"}):
                </Text>
                <TextInput
                  placeholder="0"
                  keyboardType="numeric"
                  value={displayScore}
                  onChangeText={(text) =>
                    updateGrading(qid, { teacher_score: parseInt(text, 10) || 0 })
                  }
                  style={{
                    borderWidth: 1,
                    borderColor: "#aaa",
                    padding: 5,
                    backgroundColor: "#fff",
                    width: 80,
                  }}
                />
              </View>
            );
          })}
        </ScrollView>

        {/* Submit Grades (selected attempt only) */}
        <TouchableOpacity
          onPress={async () => {
            if (!selectedAttempt) return;
            const attemptId = selectedAttempt.id;
            const edits = getEditsFor(attemptId);

            // Build graded_answers by walking attempt.answers (keeps order + ensures coverage)
            const graded_answers = (selectedAttempt.answers || []).map((entry) => {
              const qid = entry.test_question_id;
              const maxScore = getMaxScore(entry);

              // teacher override
              const overrideScore = edits[qid]?.teacher_score;
              const overrideComment = edits[qid]?.teacher_comment;

              // existing saved
              const existingScore =
                entry.teacher_score !== null && entry.teacher_score !== undefined
                  ? Number(entry.teacher_score)
                  : undefined;

              // auto-suggest
              const { suggested } = autoSuggestScore(entry);

              let finalScore =
                overrideScore !== undefined ? overrideScore
                : existingScore !== undefined ? existingScore
                : suggested !== undefined ? suggested
                : 0;

              if (!Number.isFinite(finalScore)) finalScore = 0;
              finalScore = Math.max(0, Math.min(maxScore, finalScore));

              const finalComment = overrideComment ?? entry.teacher_comment ?? "";

              return {
                test_question_id: parseInt(qid, 10),
                teacher_score: finalScore,
                teacher_comment: finalComment,
              };
            });

            const payload = {
              student_test_id: selectedAttempt.id,
              teacher_email: user_id,
              graded_answers,
            };

            try {
              await axios.post(`${FASTAPI_BASE_URL}/teacher/grade-student-test`, payload);
              Alert.alert("✅ Success", "Grades saved.");
              // Clear dirty flag (and optionally clear edits for this attempt)
              markDirty(attemptId, false);
              // setGradingEdits((prev) => ({ ...prev, [attemptId]: {} })); // optionally wipe
            } catch (err) {
              console.error("❌ Grading error:", err);
              Alert.alert("Error", "Failed to save grades.");
            }
          }}
          style={{
            marginTop: 12,
            backgroundColor: "#007BFF",
            padding: 10,
            borderRadius: 6,
          }}
        >
          <Text style={{ color: "#fff", textAlign: "center", fontWeight: "600" }}>💾 Submit Grades</Text>
        </TouchableOpacity>
      </View>
    );
  };

  // ---------------------------
  // Layout
  // ---------------------------
  return (
    <View style={{ marginTop: 20 }}>
      <Text style={styles(isDarkMode).sectionTitle}>Test Results (Test ID: {testId})</Text>

      {loadingResults ? (
        <ActivityIndicator size="large" color="#007BFF" style={{ marginTop: 12 }} />
      ) : results.length === 0 ? (
        <Text style={{ marginTop: 12, color: isDarkMode ? "#fff" : "#000" }}>
          No results available.
        </Text>
      ) : (
        <View
          style={{
            marginTop: 12,
            borderWidth: 1,
            borderColor: isDarkMode ? "#2d2d2d" : "#e5e7eb",
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              minHeight: 400,
              maxHeight: 700,
              backgroundColor: isDarkMode ? "#1f2937" : "#fff",
            }}
          >
            {/* LEFT: Students + Attempts */}
            <View
              style={{
                width: leftPanelWidth,
                borderRightWidth: 1,
                borderRightColor: isDarkMode ? "#374151" : "#e5e7eb",
                backgroundColor: isDarkMode ? "#111827" : "#f9fafb",
              }}
            >
              <ScrollView contentContainerStyle={{ padding: 10 }}>
                {grouped.map(({ email, attempts }) => {
                  const latest = attempts[0];
                  const pct =
                    latest && latest.total_score
                      ? Math.round((latest.score / latest.total_score) * 100)
                      : 0;
                  return (
                    <View
                      key={email}
                      style={{
                        marginBottom: 10,
                        borderWidth: 1,
                        borderColor: isDarkMode ? "#374151" : "#e5e7eb",
                        borderRadius: 8,
                        overflow: "hidden",
                        backgroundColor: isDarkMode ? "#0b1220" : "#fff",
                      }}
                    >
                      {/* Student header */}
                      <View style={{ padding: 10, backgroundColor: isDarkMode ? "#0f172a" : "#eef2ff" }}>
                        <Text style={{ color: isDarkMode ? "#fff" : "#111827", fontWeight: "700" }}>
                          {email}
                        </Text>
                        <Text style={{ color: isDarkMode ? "#d1d5db" : "#374151", fontSize: 12 }}>
                          Latest: {latest?.score}/{latest?.total_score} ({pct}%)
                        </Text>
                      </View>

                      {/* Attempts list */}
                      <View style={{ padding: 8 }}>
                        {attempts.map((a) => {
                          const sel = a.id === selectedAttemptId;
                          const apct =
                            a && a.total_score ? Math.round((a.score / a.total_score) * 100) : 0;
                          return (
                            <TouchableOpacity
                              key={a.id}
                              onPress={() => handleSelectAttempt(a)}
                              style={{
                                paddingVertical: 8,
                                paddingHorizontal: 10,
                                borderRadius: 6,
                                marginBottom: 6,
                                backgroundColor: sel
                                  ? "#2563eb"
                                  : isDarkMode
                                  ? "#111827"
                                  : "#f3f4f6",
                              }}
                            >
                              <Text style={{ color: sel ? "#fff" : isDarkMode ? "#e5e7eb" : "#111827" }}>
                                Attempt #{a.__attemptNo} • {fmtDate(a.completed_at)}
                              </Text>
                              <Text
                                style={{
                                  color: sel ? "#e5e7eb" : isDarkMode ? "#cbd5e1" : "#374151",
                                  fontSize: 12,
                                }}
                              >
                                {a.score}/{a.total_score} ({apct}%)
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    </View>
                  );
                })}
              </ScrollView>
            </View>

            {/* RIGHT: Selected attempt details */}
            <View style={{ flex: 1, backgroundColor: isDarkMode ? "#0b1220" : "#fff" }}>
              {renderAttemptDetail(selectedAttempt)}
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

export default TeacherTestReport;


// import React, { useEffect, useMemo, useState } from "react";
// import {
//   View,
//   Text,
//   ScrollView,
//   TouchableOpacity,
//   ActivityIndicator,
//   TextInput,
//   Platform,
//   Linking,
//   Alert,
// } from "react-native";
// import { MaterialCommunityIcons } from "@expo/vector-icons";
// import axios from "axios";

// // ⬇️ Adjust these imports if your paths are different
// import { useAuth } from "./AuthContext";
// import { FASTAPI_BASE_URL, PDF_BASE_URL } from "./config";

// /**
//  * TeacherTestReport (V2)
//  * - Left pane: grouped students and attempts
//  * - Right pane: details for selected attempt only
//  */
// const TeacherTestReport = ({ testId = null, isDarkMode, styles }) => {
//   const [results, setResults] = useState([]);
//   const [loadingResults, setLoadingResults] = useState(false);
//   const [gradingState, setGradingState] = useState({});
//   const [selectedAttemptId, setSelectedAttemptId] = useState(null);

//   const [currentAttemptId, setCurrentAttemptId] = useState(null);
//   // { [attemptId]: { [test_question_id]: { teacher_score, teacher_comment } } }
//   const [gradingEdits, setGradingEdits] = useState({});
//   // { [attemptId]: true/false }
//   const [unsaved, setUnsaved] = useState({});

//   const auth = useAuth();
//   if (!auth) {
//     console.error("⚠️ AuthContext is missing! Ensure AuthProvider wraps the app.");
//     return <Text>⚠️ Authentication error. Please login again.</Text>;
//   }
//   const { user } = auth;
//   const user_id = user?.email || "unknown_teacher";
//   const getEditsFor = (attemptId) => gradingEdits[attemptId] || {};

//   const markDirty = (attemptId, dirty = true) =>
//     setUnsaved((prev) => ({ ...prev, [attemptId]: dirty }));

//   const updateGrading = (qid, data) => {
//     if (!currentAttemptId) return;
//     setGradingEdits((prev) => {
//       const perAttempt = prev[currentAttemptId] || {};
//       const prevQ = perAttempt[qid] || {};
//       return {
//         ...prev,
//         [currentAttemptId]: { ...perAttempt, [qid]: { ...prevQ, ...data } },
//       };
//     });
//     markDirty(currentAttemptId, true);
//   };

//   // simple confirm that works on web & native
//   const confirmDiscard = async () => {
//     if (!unsaved[currentAttemptId]) return true;
//     if (Platform.OS === "web") {
//       return window.confirm("You have unsaved grading changes for this attempt. Discard them?");
//     }
//     return new Promise((resolve) => {
//       Alert.alert(
//         "Unsaved changes",
//         "You have unsaved grading changes for this attempt. Discard them?",
//         [
//           { text: "Cancel", style: "cancel", onPress: () => resolve(false) },
//           { text: "Discard", style: "destructive", onPress: () => resolve(true) },
//         ]
//       );
//     });
//   };

//   useEffect(() => {
//     const fetchResults = async () => {
//       if (!testId) return;
//       setLoadingResults(true);
//       try {
//         const res = await axios.get(`${FASTAPI_BASE_URL}/tests/${testId}/results`);
//         setResults(res.data || []);
//       } catch (error) {
//         console.error("Error fetching test results:", error);
//       } finally {
//         setLoadingResults(false);
//       }
//     };
//     fetchResults();
//   }, [testId]);

//   // Group by student email, sort attempts by completed_at (newest first)
//   const grouped = useMemo(() => {
//     const byStudent = new Map();
//     for (const r of results) {
//       const email = r.student_email || "unknown_student";
//       if (!byStudent.has(email)) byStudent.set(email, []);
//       byStudent.get(email).push(r);
//     }
//     const arr = Array.from(byStudent.entries()).map(([email, attempts]) => {
//       attempts.sort((a, b) => {
//         const ta = a.completed_at ? new Date(a.completed_at).getTime() : 0;
//         const tb = b.completed_at ? new Date(b.completed_at).getTime() : 0;
//         return tb - ta; // newest first
//       });
//       // tag attemptNo (1 = latest)
//       attempts.forEach((a, idx) => (a.__attemptNo = idx + 1));
//       return { email, attempts };
//     });
//     // sort students by name/email ASC
//     arr.sort((a, b) => a.email.localeCompare(b.email));
//     return arr;
//   }, [results]);

//   // Auto-select first attempt on load if none selected
//   useEffect(() => {
//     if (!selectedAttemptId && grouped.length > 0 && grouped[0].attempts.length > 0) {
//       setSelectedAttemptId(grouped[0].attempts[0].id);
//     }
//   }, [grouped, selectedAttemptId]);

//   const selectedAttempt = useMemo(
//     () => results.find((r) => r.id === selectedAttemptId) || null,
//     [results, selectedAttemptId]
//   );

//   // === Helpers ===
//   const fmtDate = (d) => {
//     if (!d) return "N/A";
//     try {
//       return new Date(d).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
//     } catch {
//       return String(d);
//     }
//   };
//   const optionToText = (optObj) => {
//     if (!optObj) return "";
//     if (Array.isArray(optObj.segments)) {
//       const text = optObj.segments
//         .filter((seg) => seg && seg.type === "Text" && typeof seg.text === "string")
//         .map((seg) => seg.text)
//         .join(" ")
//         .trim();
//       if (text) return text;
//     }
//     return optObj.text || "";
//   };

//   // Build the per-question detail block reusing your autograde + prefill logic
//   const renderAttemptDetail = (attempt) => {
//     if (!attempt) {
//       return (
//         <View style={{ padding: 12 }}>
//           <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
//             Select a student attempt to view details.
//           </Text>
//         </View>
//       );
//     }

//     return (
//       <View style={{ padding: 12 }}>
//         {/* Summary header */}
//         <View
//           style={{
//             backgroundColor: isDarkMode ? "#2b2b2b" : "#f5f7fb",
//             padding: 12,
//             borderRadius: 8,
//             borderWidth: 1,
//             borderColor: isDarkMode ? "#3a3a3a" : "#e1e6ef",
//             marginBottom: 12,
//           }}
//         >
//           <Text style={{ color: isDarkMode ? "#fff" : "#000", fontWeight: "700", fontSize: 16 }}>
//             {attempt.student_email}
//           </Text>
//           <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
//             Attempt #{attempt.__attemptNo} • Submitted: {fmtDate(attempt.completed_at)}
//           </Text>
//           <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
//             Score: {attempt.score} / {attempt.total_score} ({Math.round((attempt.score / Math.max(1, attempt.total_score)) * 100)}%)
//           </Text>
//           <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>Time Taken: {attempt.time_taken} sec</Text>

//           {/* PDF links */}
//           <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12, marginTop: 8 }}>
//             {attempt.pdf_path ? (
//               <TouchableOpacity
//                 onPress={() => {
//                   try {
//                     const url = `${PDF_BASE_URL}/${attempt.pdf_path}`;
//                     if (Platform.OS === "web") window.open(url, "_blank");
//                     else Linking.openURL(url);
//                   } catch (err) {
//                     console.error("PDF open error", err);
//                     Alert.alert("Error", "Could not open PDF file.");
//                   }
//                 }}
//                 style={{ paddingVertical: 6, paddingHorizontal: 10, backgroundColor: "#111827", borderRadius: 6 }}
//               >
//                 <Text style={{ color: "#fff" }}>📄 View Submitted PDF</Text>
//               </TouchableOpacity>
//             ) : null}

//             {attempt.graded_pdf_path ? (
//               <TouchableOpacity
//                 onPress={() => {
//                   const url = `${PDF_BASE_URL}/${attempt.graded_pdf_path}`;
//                   if (Platform.OS === "web") window.open(url, "_blank");
//                   else Linking.openURL(url);
//                 }}
//                 style={{ paddingVertical: 6, paddingHorizontal: 10, backgroundColor: "#14532d", borderRadius: 6 }}
//               >
//                 <Text style={{ color: "#fff" }}>📄 View Graded PDF</Text>
//               </TouchableOpacity>
//             ) : null}
//           </View>
//         </View>

//         {/* Questions list (ONLY for this attempt) */}
//         <ScrollView
//           style={{ maxHeight: 520, borderWidth: 1, borderColor: "#ddd", borderRadius: 8 }}
//           contentContainerStyle={{ padding: 12 }}
//         >
//           {(attempt.answers || []).map((entry, idxQuestion) => {
//             const qid = entry.test_question_id;
//             const displayNumber = idxQuestion + 1;

//             // Parse question text we showed the student
//             let parsedQuestionPayload = {};
//             try {
//               parsedQuestionPayload = entry.question_text ? JSON.parse(entry.question_text) : {};
//             } catch {}
//             const questionStemText = parsedQuestionPayload.text || "[Question text missing]";

//             // Parse options
//             let parsedOptions = null;
//             try {
//               parsedOptions = entry.options ? JSON.parse(entry.options) : null;
//             } catch {}

//             // Parse student answer (from DB string/json)
//             let rawStudentAns;
//             try {
//               rawStudentAns = entry.student_answer ? JSON.parse(entry.student_answer) : null;
//             } catch {
//               rawStudentAns = entry.student_answer;
//             }

//             // Student answer display
//             let finalStudentAnswerDisplay = "No answer";
//             if (rawStudentAns == null) {
//               finalStudentAnswerDisplay = "No answer";
//             } else if (
//               typeof rawStudentAns === "string" &&
//               rawStudentAns.startsWith("option") &&
//               parsedOptions
//             ) {
//               const stuOptKey = rawStudentAns;
//               const stuOptObj = parsedOptions[stuOptKey];
//               const stuText = optionToText(stuOptObj) || stuOptKey;
//               const m = stuOptKey.match(/^option(\d+)$/i);
//               finalStudentAnswerDisplay = m && m[1] ? `(Option ${m[1]}) ${stuText}` : stuText;
//             } else if (Array.isArray(rawStudentAns)) {
//               finalStudentAnswerDisplay = rawStudentAns.join(", ");
//             } else {
//               finalStudentAnswerDisplay = String(rawStudentAns);
//             }

//             // Correct answer display + canonical value
//             let finalCorrectAnswerDisplay = "[N/A]";
//             let correctAnswerCanonical = null;
//             if (entry.question_type === "2" || entry.question_type === "4") {
//               if (parsedOptions?.correct_answer != null) {
//                 const corr = parsedOptions.correct_answer;
//                 correctAnswerCanonical = corr;
//                 if (typeof corr === "string" && /^\d+$/.test(corr) && parsedOptions) {
//                   const corrKey = "option" + corr;
//                   const corrOptObj = parsedOptions[corrKey];
//                   const corrText = optionToText(corrOptObj) || corrKey;
//                   finalCorrectAnswerDisplay = `(Option ${corr}) ${corrText}`;
//                 } else if (typeof corr === "string" && corr.startsWith("option") && parsedOptions) {
//                   const corrOptObj = parsedOptions[corr];
//                   const corrText = optionToText(corrOptObj) || corr;
//                   const m = corr.match(/^option(\d+)$/i);
//                   finalCorrectAnswerDisplay = m && m[1] ? `(Option ${m[1]}) ${corrText}` : corrText;
//                 } else {
//                   finalCorrectAnswerDisplay = String(corr);
//                 }
//               }
//             } else if (entry.question_type === "1") {
//               if (parsedOptions?.correct_answer != null) {
//                 correctAnswerCanonical = parsedOptions.correct_answer;
//                 finalCorrectAnswerDisplay = String(parsedOptions.correct_answer);
//               }
//             } else if (entry.question_type === "10" || entry.question_type === "11") {
//               finalCorrectAnswerDisplay = "[Teacher graded]";
//             } else if (entry.question_type === "9") {
//               finalCorrectAnswerDisplay = "[See pairs]";
//             }

//             // Max score (default 1)
//             const maxScoreForThisQ = entry.max_score != null ? entry.max_score : 1;
//             const maxScoreNum = Number.isFinite(maxScoreForThisQ)
//               ? maxScoreForThisQ
//               : parseInt(maxScoreForThisQ, 10);

//             // Autograde guess
//             let isAutoGradable = false;
//             let isCorrectLocal = false;
//             if (entry.question_type === "1") {
//               isAutoGradable = true;
//               if (rawStudentAns != null && correctAnswerCanonical != null) {
//                 isCorrectLocal =
//                   String(rawStudentAns).trim().toLowerCase() ===
//                   String(correctAnswerCanonical).trim().toLowerCase();
//               }
//             } else if (entry.question_type === "2") {
//               isAutoGradable = true;
//               let stuNum = null;
//               if (typeof rawStudentAns === "string" && rawStudentAns.startsWith("option")) {
//                 const m = rawStudentAns.match(/^option(\d+)$/i);
//                 if (m && m[1]) stuNum = parseInt(m[1], 10);
//               } else if (typeof rawStudentAns === "string" && /^\d+$/.test(rawStudentAns)) {
//                 stuNum = parseInt(rawStudentAns, 10);
//               }
//               let corrNum = null;
//               if (typeof correctAnswerCanonical === "string" && /^\d+$/.test(correctAnswerCanonical)) {
//                 corrNum = parseInt(correctAnswerCanonical, 10);
//               } else if (
//                 typeof correctAnswerCanonical === "string" &&
//                 correctAnswerCanonical.startsWith("option")
//               ) {
//                 const m2 = correctAnswerCanonical.match(/^option(\d+)$/i);
//                 if (m2 && m2[1]) corrNum = parseInt(m2[1], 10);
//               }
//               if (Number.isFinite(stuNum) && Number.isFinite(corrNum)) {
//                 isCorrectLocal = stuNum === corrNum;
//               }
//             } else if (entry.question_type === "4") {
//               isAutoGradable = true;
//               if (rawStudentAns != null && correctAnswerCanonical != null) {
//                 isCorrectLocal =
//                   String(rawStudentAns).trim().toLowerCase() ===
//                   String(correctAnswerCanonical).trim().toLowerCase();
//               }
//             }

//             // Auto suggested score
//             let autoSuggestedScore = "";
//             if (isAutoGradable && Number.isFinite(maxScoreNum)) {
//               autoSuggestedScore = isCorrectLocal ? maxScoreNum : 0;
//             }

//             // Prefills: gradingState > saved teacher_score > autoSuggestedScore > ""
//             const prefilledScore =
//               gradingState[qid]?.teacher_score !== undefined
//                 ? String(gradingState[qid].teacher_score)
//                 : entry.teacher_score !== null && entry.teacher_score !== undefined
//                 ? String(entry.teacher_score)
//                 : autoSuggestedScore !== ""
//                 ? String(autoSuggestedScore)
//                 : "";

//             const prefilledComment =
//               gradingState[qid]?.teacher_comment !== undefined
//                 ? gradingState[qid].teacher_comment
//                 : entry.teacher_comment || "";

//             return (
//               <View
//                 key={qid}
//                 style={{
//                   paddingVertical: 10,
//                   borderBottomWidth: 1,
//                   borderColor: isDarkMode ? "#333" : "#eee",
//                 }}
//               >
//                 <Text
//                   style={{
//                     color: isDarkMode ? "#fff" : "#000",
//                     fontWeight: "600",
//                   }}
//                 >
//                   Q{displayNumber}: {questionStemText}
//                 </Text>

//                 <Text
//                   style={{
//                     color: isDarkMode ? "#fff" : "#000",
//                     fontStyle: "italic",
//                     marginBottom: 4,
//                   }}
//                 >
//                   Points possible: {Number.isFinite(maxScoreNum) ? maxScoreNum : "?"}
//                 </Text>

//                 <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
//                   Student Answer: {finalStudentAnswerDisplay}
//                 </Text>

//                 <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
//                   Correct Answer: {finalCorrectAnswerDisplay}
//                 </Text>

//                 <TextInput
//                   placeholder="Teacher Comment"
//                   value={prefilledComment}
//                   onChangeText={(text) =>
//                     setGradingState((prev) => ({
//                       ...prev,
//                       [qid]: {
//                         ...prev[qid],
//                         teacher_comment: text,
//                       },
//                     }))
//                   }
//                   style={{
//                     borderWidth: 1,
//                     borderColor: "#aaa",
//                     padding: 5,
//                     marginTop: 6,
//                     marginBottom: 6,
//                     backgroundColor: "#fff",
//                   }}
//                 />

//                 <Text
//                   style={{
//                     color: isDarkMode ? "#fff" : "#000",
//                     marginTop: 4,
//                     marginBottom: 4,
//                     fontWeight: "500",
//                   }}
//                 >
//                   Score (out of {Number.isFinite(maxScoreNum) ? maxScoreNum : "?"}):
//                 </Text>
//                 <TextInput
//                   placeholder="0"
//                   keyboardType="numeric"
//                   value={prefilledScore}
//                   onChangeText={(text) =>
//                     setGradingState((prev) => ({
//                       ...prev,
//                       [qid]: {
//                         ...prev[qid],
//                         teacher_score: parseInt(text, 10) || 0,
//                       },
//                     }))
//                   }
//                   style={{
//                     borderWidth: 1,
//                     borderColor: "#aaa",
//                     padding: 5,
//                     backgroundColor: "#fff",
//                     width: 80,
//                   }}
//                 />
//               </View>
//             );
//           })}
//         </ScrollView>

//         {/* Submit Grades button for this attempt */}
//         <TouchableOpacity
//           onPress={async () => {
//             const payload = {
//               student_test_id: attempt.id,
//               teacher_email: user_id,
//               graded_answers: Object.entries(gradingState).map(([test_question_id, data]) => ({
//                 test_question_id: parseInt(test_question_id, 10),
//                 teacher_score: parseInt(data.teacher_score, 10) || 0,
//                 teacher_comment: data.teacher_comment || "",
//               })),
//             };
//             try {
//               await axios.post(`${FASTAPI_BASE_URL}/teacher/grade-student-test`, payload);
//               Alert.alert("✅ Success", "Grades saved.");
//             } catch (err) {
//               console.error("❌ Grading error:", err);
//               Alert.alert("Error", "Failed to save grades.");
//             }
//           }}
//           style={{
//             marginTop: 12,
//             backgroundColor: "#007BFF",
//             padding: 10,
//             borderRadius: 6,
//           }}
//         >
//           <Text style={{ color: "#fff", textAlign: "center", fontWeight: "600" }}>💾 Submit Grades</Text>
//         </TouchableOpacity>
//       </View>
//     );
//   };

//   return (
//     <View style={{ marginTop: 20 }}>
//       <Text style={styles(isDarkMode).sectionTitle}>Test Results (Test ID: {testId})</Text>

//       {loadingResults ? (
//         <ActivityIndicator size="large" color="#007BFF" style={{ marginTop: 12 }} />
//       ) : results.length === 0 ? (
//         <Text style={{ marginTop: 12, color: isDarkMode ? "#fff" : "#000" }}>No results available.</Text>
//       ) : (
//         <View
//           style={{
//             marginTop: 12,
//             borderWidth: 1,
//             borderColor: isDarkMode ? "#2d2d2d" : "#e5e7eb",
//             borderRadius: 10,
//             overflow: "hidden",
//           }}
//         >
//           <View
//             style={{
//               flexDirection: "row",
//               minHeight: 400,
//               maxHeight: 700,
//               backgroundColor: isDarkMode ? "#1f2937" : "#fff",
//             }}
//           >
//             {/* LEFT: Students + Attempts */}
//             <View
//               style={{
//                 width: 340,
//                 borderRightWidth: 1,
//                 borderRightColor: isDarkMode ? "#374151" : "#e5e7eb",
//                 backgroundColor: isDarkMode ? "#111827" : "#f9fafb",
//               }}
//             >
//               <ScrollView contentContainerStyle={{ padding: 10 }}>
//                 {grouped.map(({ email, attempts }) => {
//                   const latest = attempts[0];
//                   const pct = latest && latest.total_score
//                     ? Math.round((latest.score / latest.total_score) * 100)
//                     : 0;
//                   return (
//                     <View
//                       key={email}
//                       style={{
//                         marginBottom: 10,
//                         borderWidth: 1,
//                         borderColor: isDarkMode ? "#374151" : "#e5e7eb",
//                         borderRadius: 8,
//                         overflow: "hidden",
//                         backgroundColor: isDarkMode ? "#0b1220" : "#fff",
//                       }}
//                     >
//                       {/* Student header */}
//                       <View style={{ padding: 10, backgroundColor: isDarkMode ? "#0f172a" : "#eef2ff" }}>
//                         <Text style={{ color: isDarkMode ? "#fff" : "#111827", fontWeight: "700" }}>
//                           {email}
//                         </Text>
//                         <Text style={{ color: isDarkMode ? "#d1d5db" : "#374151", fontSize: 12 }}>
//                           Latest: {latest?.score}/{latest?.total_score} ({pct}%)
//                         </Text>
//                       </View>

//                       {/* Attempts list */}
//                       <View style={{ padding: 8 }}>
//                         {attempts.map((a) => {
//                           const sel = a.id === selectedAttemptId;
//                           const apct =
//                             a && a.total_score ? Math.round((a.score / a.total_score) * 100) : 0;
//                           return (
//                             <TouchableOpacity
//                               key={a.id}
//                               onPress={() => setSelectedAttemptId(a.id)}
//                               style={{
//                                 paddingVertical: 8,
//                                 paddingHorizontal: 10,
//                                 borderRadius: 6,
//                                 marginBottom: 6,
//                                 backgroundColor: sel
//                                   ? "#2563eb"
//                                   : isDarkMode
//                                   ? "#111827"
//                                   : "#f3f4f6",
//                               }}
//                             >
//                               <Text style={{ color: sel ? "#fff" : isDarkMode ? "#e5e7eb" : "#111827" }}>
//                                 Attempt #{a.__attemptNo} • {fmtDate(a.completed_at)}
//                               </Text>
//                               <Text
//                                 style={{
//                                   color: sel ? "#e5e7eb" : isDarkMode ? "#cbd5e1" : "#374151",
//                                   fontSize: 12,
//                                 }}
//                               >
//                                 {a.score}/{a.total_score} ({apct}%)
//                               </Text>
//                             </TouchableOpacity>
//                           );
//                         })}
//                       </View>
//                     </View>
//                   );
//                 })}
//               </ScrollView>
//             </View>

//             {/* RIGHT: Selected attempt details */}
//             <View style={{ flex: 1, backgroundColor: isDarkMode ? "#0b1220" : "#fff" }}>
//               {renderAttemptDetail(selectedAttempt)}
//             </View>
//           </View>
//         </View>
//       )}
//     </View>
//   );
// };

// export default TeacherTestReport;


// // // TeacherTestReport.js
// // import React, { useState, useEffect } from "react";
// // import axios from "axios";
// // import { FASTAPI_BASE_URL, PDF_BASE_URL } from "./config";
// // import {
// //   TouchableOpacity,
// //   Text,
// //   View,
// //   ScrollView,
// //   ActivityIndicator,
// //   Alert,
// //   TextInput,
// //   Platform,
// //   Linking,
// // } from "react-native";
// // import { useAuth } from "./AuthContext"; // adjust path if needed

// // const TeacherTestReport = ({ testId = null, isDarkMode, styles }) => {
// //   const [results, setResults] = useState([]);
// //   const [loadingResults, setLoadingResults] = useState(false);
// //   const [gradingState, setGradingState] = useState({});

// //   useEffect(() => {
// //     const fetchResults = async () => {
// //       if (!testId) return;
// //       setLoadingResults(true);
// //       try {
// //         const res = await axios.get(`${FASTAPI_BASE_URL}/tests/${testId}/results`);
// //         setResults(res.data);
// //       } catch (error) {
// //         console.error("Error fetching test results:", error);
// //       } finally {
// //         setLoadingResults(false);
// //       }
// //     };
// //     fetchResults();
// //   }, [testId]);

// //   const auth = useAuth();

// //   if (!auth) {
// //     console.error("⚠️ AuthContext is missing! Ensure AuthProvider wraps the app.");
// //     return <Text>⚠️ Authentication error. Please login again.</Text>;
// //   }

// //   const { user } = auth;
// //   const user_id = user?.email || "unknown_teacher";

// //   const updateGrading = (qid, data) => {
// //     setGradingState((prev) => ({
// //       ...prev,
// //       [qid]: { ...prev[qid], ...data },
// //     }));
// //   };

// //   const submitGrades = async (studentTestId) => {
// //     const payload = {
// //       student_test_id: studentTestId,
// //       teacher_email: user_id,  // ✅ Include teacher's ID
// //       graded_answers: Object.entries(gradingState).map(([test_question_id, data]) => ({
// //         test_question_id: parseInt(test_question_id),
// //         teacher_score: parseInt(data.teacher_score) || 0,
// //         teacher_comment: data.teacher_comment || "",
// //       })),
// //     };

// //     try {
// //       await axios.post(`${FASTAPI_BASE_URL}/teacher/grade-student-test`, payload);
// //       Alert.alert("✅ Success", "Grades saved.");
// //     } catch (err) {
// //       console.error("❌ Grading error:", err);
// //       Alert.alert("Error", "Failed to save grades.");
// //     }
// //   };

// //   return (
// //     <View style={{ marginTop: 20 }}>
// //       <Text style={styles(isDarkMode).sectionTitle}>
// //         Test Results (Test ID: {testId})
// //       </Text>
// //       {loadingResults ? (
// //         <ActivityIndicator size="large" color="#007BFF" />
// //       ) : (
// //         <ScrollView
// //           style={{
// //             maxHeight: 500,
// //             borderWidth: 1,
// //             borderColor: "#ccc",
// //             padding: 10,
// //             marginTop: 10,
// //           }}
// //         >
// //           {results.length === 0 ? (
// //             <Text>No results available.</Text>
// //           ) : (
// //             results.map((result) => (
// //               <View
// //                 key={result.id}
// //                 style={{
// //                   marginBottom: 15,
// //                   padding: 10,
// //                   backgroundColor: isDarkMode ? "#333" : "#f9f9f9",
// //                   borderRadius: 5,
// //                 }}
// //               >
// //                 {/* 🧾 Summary Info */}
// //                 <Text style={{ color: isDarkMode ? "#fff" : "#000", fontWeight: "bold" }}>
// //                   Student Email: {result.student_email}
// //                 </Text>
// //                 <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
// //                   Test Title: {result.test_title || "N/A"}
// //                 </Text>
// //                 <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
// //                   Score: {result.score} / {result.total_score}
// //                 </Text>
// //                 <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
// //                   Time Taken: {result.time_taken} sec
// //                 </Text>
// //                 <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
// //                   Submitted At: {result.completed_at
// //                     ? new Date(result.completed_at).toLocaleString("en-US", {
// //                         dateStyle: "medium",
// //                         timeStyle: "short",
// //                       })
// //                     : "N/A"}
// //                 </Text>

           
               
// //                 {results.length === 0 ? (
// //                   <Text>No results available.</Text>
// //                 ) : (
// //                   results.map((result) => (
// //                     <View
// //                       key={result.id}
// //                       style={{
// //                         marginBottom: 15,
// //                         padding: 10,
// //                         backgroundColor: isDarkMode ? "#333" : "#f9f9f9",
// //                         borderRadius: 5,
// //                       }}
// //                     >
// //                       {/* 🧾 Summary Info */}
// //                       <Text
// //                         style={{
// //                           color: isDarkMode ? "#fff" : "#000",
// //                           fontWeight: "bold",
// //                         }}
// //                       >
// //                         Student Email: {result.student_email}
// //                       </Text>

// //                       <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
// //                         Test Title: {result.test_title || "N/A"}
// //                       </Text>

// //                       <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
// //                         Score: {result.score} / {result.total_score}
// //                       </Text>

// //                       <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
// //                         Time Taken: {result.time_taken} sec
// //                       </Text>

// //                       <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
// //                         Submitted At:{" "}
// //                         {result.completed_at
// //                           ? new Date(result.completed_at).toLocaleString("en-US", {
// //                               dateStyle: "medium",
// //                               timeStyle: "short",
// //                             })
// //                           : "N/A"}
// //                       </Text>

// //                       {/* 🔍 Per-question breakdown */}
// //                       {result.answers?.map((entry, idxQuestion) => {
// //                         const qid = entry.test_question_id;
// //                         const displayNumber = idxQuestion + 1; // Q1, Q2, ...

// //                         // 1. Parse stored question text we showed student
// //                         let parsedQuestionPayload = {};
// //                         try {
// //                           parsedQuestionPayload = entry.question_text
// //                             ? JSON.parse(entry.question_text)
// //                             : {};
// //                         } catch (e) {
// //                           console.warn("⚠ question_text parse fail for", qid, e);
// //                         }
// //                         const questionStemText =
// //                           parsedQuestionPayload.text || "[Question text missing]";

// //                         // 2. Parse options (MCQ, T/F, etc.)
// //                         let parsedOptions = null;
// //                         try {
// //                           parsedOptions = entry.options ? JSON.parse(entry.options) : null;
// //                         } catch (e) {
// //                           console.warn("⚠ options parse fail for", qid, e);
// //                         }

// //                         // helper to stringify an option object nicely
// //                         function optionToText(optObj) {
// //                           if (!optObj) return "";
// //                           // Prefer stitched segment text
// //                           if (Array.isArray(optObj.segments)) {
// //                             const segText = optObj.segments
// //                               .filter(
// //                                 (seg) => seg && seg.type === "Text" && typeof seg.text === "string"
// //                               )
// //                               .map((seg) => seg.text)
// //                               .join(" ")
// //                               .trim();
// //                             if (segText.length > 0) return segText;
// //                           }
// //                           // fallback
// //                           if (optObj.text) return optObj.text;
// //                           return "";
// //                         }

// //                         // 3. Parse student answer from DB (stringified JSON)
// //                         let rawStudentAns;
// //                         try {
// //                           rawStudentAns = entry.student_answer
// //                             ? JSON.parse(entry.student_answer)
// //                             : null;
// //                         } catch (e) {
// //                           rawStudentAns = entry.student_answer;
// //                         }

// //                         // Build pretty "Student Answer: (Option 2) Text..."
// //                         // and "Correct Answer: (Option 3) Text..."
// //                         let finalStudentAnswerDisplay = "No answer";
// //                         if (rawStudentAns == null) {
// //                           finalStudentAnswerDisplay = "No answer";
// //                         } else if (
// //                           typeof rawStudentAns === "string" &&
// //                           rawStudentAns.startsWith("option") &&
// //                           parsedOptions
// //                         ) {
// //                           // e.g. "option3"
// //                           const stuOptKey = rawStudentAns;
// //                           const stuOptObj = parsedOptions[stuOptKey];
// //                           const stuText = optionToText(stuOptObj) || stuOptKey;

// //                           let stuOptNum = "";
// //                           const m = stuOptKey.match(/^option(\d+)$/i);
// //                           if (m && m[1]) stuOptNum = m[1];

// //                           finalStudentAnswerDisplay = stuOptNum
// //                             ? `(Option ${stuOptNum}) ${stuText}`
// //                             : stuText;
// //                         } else if (Array.isArray(rawStudentAns)) {
// //                           // matching-style or multi-part answers
// //                           finalStudentAnswerDisplay = rawStudentAns.join(", ");
// //                         } else {
// //                           finalStudentAnswerDisplay = String(rawStudentAns);
// //                         }

// //                         let finalCorrectAnswerDisplay = "[N/A]";
// //                         let correctAnswerCanonical = null; // we'll also use this for auto-grading

// //                         if (entry.question_type === "2" || entry.question_type === "4") {
// //                           // MCQ or True/False style
// //                           if (parsedOptions?.correct_answer != null) {
// //                             const corr = parsedOptions.correct_answer;
// //                             correctAnswerCanonical = corr;

// //                             if (
// //                               typeof corr === "string" &&
// //                               corr.match(/^\d+$/) &&
// //                               parsedOptions
// //                             ) {
// //                               // corr like "2" → option2
// //                               const corrKey = "option" + corr;
// //                               const corrOptObj = parsedOptions[corrKey];
// //                               const corrText = optionToText(corrOptObj) || corrKey;
// //                               finalCorrectAnswerDisplay = `(Option ${corr}) ${corrText}`;
// //                             } else if (
// //                               typeof corr === "string" &&
// //                               corr.startsWith("option") &&
// //                               parsedOptions
// //                             ) {
// //                               const corrOptObj = parsedOptions[corr];
// //                               const corrText = optionToText(corrOptObj) || corr;
// //                               const m2 = corr.match(/^option(\d+)$/i);
// //                               if (m2 && m2[1]) {
// //                                 finalCorrectAnswerDisplay = `(Option ${m2[1]}) ${corrText}`;
// //                               } else {
// //                                 finalCorrectAnswerDisplay = corrText;
// //                               }
// //                             } else {
// //                               // maybe literally "True" / "False"
// //                               finalCorrectAnswerDisplay = String(corr);
// //                             }
// //                           }
// //                         } else if (entry.question_type === "1") {
// //                           // fill-in
// //                           if (parsedOptions?.correct_answer != null) {
// //                             correctAnswerCanonical = parsedOptions.correct_answer;
// //                             finalCorrectAnswerDisplay = String(parsedOptions.correct_answer);
// //                           }
// //                         } else if (entry.question_type === "10" || entry.question_type === "11") {
// //                           // essay
// //                           finalCorrectAnswerDisplay = "[Teacher graded]";
// //                           correctAnswerCanonical = null;
// //                         } else if (entry.question_type === "9") {
// //                           // matching
// //                           finalCorrectAnswerDisplay = "[See pairs]";
// //                           correctAnswerCanonical = null;
// //                         }

// //                         // 4. Determine max score for this question:
// //                         const maxScoreForThisQ =
// //                           entry.max_score != null ? entry.max_score : 1;
// //                         const maxScoreNum = Number.isFinite(maxScoreForThisQ)
// //                           ? maxScoreForThisQ
// //                           : parseInt(maxScoreForThisQ, 10);

// //                         // 5. Auto-evaluation guess (correct or not) for autogradable types
// //                         // We'll mirror backend logic roughly:
// //                         // - type 1 (fill-in)    -> compare lowercase trimmed strings
// //                         // - type 2 (MCQ)        -> compare selected option number
// //                         // - type 4 (T/F)        -> compare lowercase text
// //                         // Others                -> not auto-grad
// //                         let isAutoGradable = false;
// //                         let isCorrectLocal = false;

// //                         if (entry.question_type === "1") {
// //                           // fill in the blank
// //                           isAutoGradable = true;
// //                           if (
// //                             rawStudentAns != null &&
// //                             correctAnswerCanonical != null
// //                           ) {
// //                             const stuNorm = String(rawStudentAns).trim().toLowerCase();
// //                             const corrNorm = String(correctAnswerCanonical)
// //                               .trim()
// //                               .toLowerCase();
// //                             isCorrectLocal = stuNorm === corrNorm;
// //                           }
// //                         } else if (entry.question_type === "2") {
// //                           // MCQ
// //                           isAutoGradable = true;

// //                           // normalize student to a number
// //                           let stuNum = null;
// //                           if (
// //                             typeof rawStudentAns === "string" &&
// //                             rawStudentAns.startsWith("option")
// //                           ) {
// //                             const m3 = rawStudentAns.match(/^option(\d+)$/i);
// //                             if (m3 && m3[1]) {
// //                               stuNum = parseInt(m3[1], 10);
// //                             }
// //                           } else if (
// //                             typeof rawStudentAns === "string" &&
// //                             /^\d+$/.test(rawStudentAns)
// //                           ) {
// //                             stuNum = parseInt(rawStudentAns, 10);
// //                           }

// //                           // normalize correct to a number
// //                           let corrNum = null;
// //                           if (
// //                             typeof correctAnswerCanonical === "string" &&
// //                             correctAnswerCanonical.match(/^\d+$/)
// //                           ) {
// //                             corrNum = parseInt(correctAnswerCanonical, 10);
// //                           } else if (
// //                             typeof correctAnswerCanonical === "string" &&
// //                             correctAnswerCanonical.startsWith("option")
// //                           ) {
// //                             const m4 = correctAnswerCanonical.match(/^option(\d+)$/i);
// //                             if (m4 && m4[1]) {
// //                               corrNum = parseInt(m4[1], 10);
// //                             }
// //                           }

// //                           if (
// //                             stuNum != null &&
// //                             corrNum != null &&
// //                             Number.isFinite(stuNum) &&
// //                             Number.isFinite(corrNum)
// //                           ) {
// //                             isCorrectLocal = stuNum === corrNum;
// //                           }
// //                         } else if (entry.question_type === "4") {
// //                           // True / False
// //                           isAutoGradable = true;
// //                           if (
// //                             rawStudentAns != null &&
// //                             correctAnswerCanonical != null
// //                           ) {
// //                             const stuNorm = String(rawStudentAns)
// //                               .trim()
// //                               .toLowerCase();
// //                             const corrNorm = String(correctAnswerCanonical)
// //                               .trim()
// //                               .toLowerCase();
// //                             isCorrectLocal = stuNorm === corrNorm;
// //                           }
// //                         }

// //                         // 6. Suggested default score:
// //                         //    If it's auto-gradable, we suggest full points if correct, else 0.
// //                         //    Only if teacher hasn't already typed something AND
// //                         //    there's no saved teacher_score.
// //                         let autoSuggestedScore = "";
// //                         if (
// //                           isAutoGradable &&
// //                           Number.isFinite(maxScoreNum)
// //                         ) {
// //                           autoSuggestedScore = isCorrectLocal ? maxScoreNum : 0;
// //                         }

// //                         // 7. Pre-fill score input priority:
// //                         //    1. what teacher is currently editing in this UI (gradingState)
// //                         //    2. what backend already saved (entry.teacher_score)
// //                         //    3. our autoSuggestedScore (for auto-gradable Qs)
// //                         const prefilledScore =
// //                           gradingState[qid]?.teacher_score !== undefined
// //                             ? String(gradingState[qid].teacher_score)
// //                             : entry.teacher_score !== null &&
// //                               entry.teacher_score !== undefined
// //                             ? String(entry.teacher_score)
// //                             : autoSuggestedScore !== ""
// //                             ? String(autoSuggestedScore)
// //                             : "";

// //                         // 8. Pre-fill teacher comment similarly (editing state > saved > "")
// //                         const prefilledComment =
// //                           gradingState[qid]?.teacher_comment !== undefined
// //                             ? gradingState[qid].teacher_comment
// //                             : entry.teacher_comment || "";

// //                         return (
// //                           <View key={qid} style={{ marginTop: 10 }}>
// //                             {/* Q# and points possible */}
// //                             <Text
// //                               style={{
// //                                 color: isDarkMode ? "#fff" : "#000",
// //                                 fontWeight: "600",
// //                               }}
// //                             >
// //                               Q{displayNumber}: {questionStemText}
// //                             </Text>

// //                             <Text
// //                               style={{
// //                                 color: isDarkMode ? "#fff" : "#000",
// //                                 fontStyle: "italic",
// //                                 marginBottom: 4,
// //                               }}
// //                             >
// //                               Points possible: {Number.isFinite(maxScoreNum) ? maxScoreNum : "?"}
// //                             </Text>

// //                             <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
// //                               Student Answer: {finalStudentAnswerDisplay}
// //                             </Text>

// //                             <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
// //                               Correct Answer: {finalCorrectAnswerDisplay}
// //                             </Text>

// //                             {/* Teacher comment box */}
// //                             <TextInput
// //                               placeholder="Teacher Comment"
// //                               value={prefilledComment}
// //                               onChangeText={(text) =>
// //                                 setGradingState((prev) => ({
// //                                   ...prev,
// //                                   [qid]: {
// //                                     ...prev[qid],
// //                                     teacher_comment: text,
// //                                   },
// //                                 }))
// //                               }
// //                               style={{
// //                                 borderWidth: 1,
// //                                 borderColor: "#aaa",
// //                                 padding: 5,
// //                                 marginVertical: 5,
// //                                 backgroundColor: "#fff",
// //                               }}
// //                             />

// //                             {/* Teacher score box */}
// //                             <Text
// //                               style={{
// //                                 color: isDarkMode ? "#fff" : "#000",
// //                                 marginTop: 8,
// //                                 marginBottom: 4,
// //                                 fontWeight: "500",
// //                               }}
// //                             >
// //                               Score (out of {Number.isFinite(maxScoreNum) ? maxScoreNum : "?"}):
// //                             </Text>
// //                             <TextInput
// //                               placeholder="0"
// //                               keyboardType="numeric"
// //                               value={prefilledScore}
// //                               onChangeText={(text) =>
// //                                 setGradingState((prev) => ({
// //                                   ...prev,
// //                                   [qid]: {
// //                                     ...prev[qid],
// //                                     teacher_score: parseInt(text, 10) || 0,
// //                                   },
// //                                 }))
// //                               }
// //                               style={{
// //                                 borderWidth: 1,
// //                                 borderColor: "#aaa",
// //                                 padding: 5,
// //                                 backgroundColor: "#fff",
// //                                 width: 80,
// //                               }}
// //                             />
// //                           </View>
// //                         );
// //                       })}

// //                       {/* Submit button for the whole student test */}
// //                       <TouchableOpacity
// //                         onPress={() => submitGrades(result.id)}
// //                         style={{
// //                           marginTop: 10,
// //                           backgroundColor: "#007BFF",
// //                           padding: 8,
// //                           borderRadius: 4,
// //                         }}
// //                       >
// //                         <Text style={{ color: "#fff", textAlign: "center" }}>
// //                           💾 Submit Grades
// //                         </Text>
// //                       </TouchableOpacity>

// //                       {/* PDF links */}
// //                       {result.pdf_path && (
// //                         <TouchableOpacity
// //                           onPress={() => {
// //                             try {
// //                               if (Platform.OS === "web") {
// //                                 window.open(`${PDF_BASE_URL}/${result.pdf_path}`, "_blank");
// //                               } else {
// //                                 Linking.openURL(`${PDF_BASE_URL}/${result.pdf_path}`);
// //                               }
// //                             } catch (err) {
// //                               console.error("Failed to open PDF:", err);
// //                               Alert.alert("Error", "Could not open PDF file.");
// //                             }
// //                           }}
// //                           style={{ marginTop: 8 }}
// //                         >
// //                           <Text style={{ color: "#007BFF" }}>📄 View Submitted PDF</Text>
// //                         </TouchableOpacity>
// //                       )}

// //                       {result.graded_pdf_path && (
// //                         <TouchableOpacity
// //                           onPress={() => {
// //                             if (Platform.OS === "web") {
// //                               window.open(
// //                                 `${PDF_BASE_URL}/${result.graded_pdf_path}`,
// //                                 "_blank"
// //                               );
// //                             } else {
// //                               Linking.openURL(
// //                                 `${PDF_BASE_URL}/${result.graded_pdf_path}`
// //                               );
// //                             }
// //                           }}
// //                           style={{ marginTop: 5 }}
// //                         >
// //                           <Text style={{ color: "#28a745" }}>
// //                             📄 View Graded PDF
// //                           </Text>
// //                         </TouchableOpacity>
// //                       )}
// //                     </View>
// //                   ))
// //                 )}

// //                 {/* Submit Button */}
// //                 <TouchableOpacity
// //                   onPress={() => submitGrades(result.id)}
// //                   style={{
// //                     marginTop: 10,
// //                     backgroundColor: "#007BFF",
// //                     padding: 8,
// //                     borderRadius: 4,
// //                   }}
// //                 >
// //                   <Text style={{ color: "#fff", textAlign: "center" }}>💾 Submit Grades</Text>
// //                 </TouchableOpacity>

// //                 {/* PDF Link */}
// //                 {result.pdf_path && (
// //                   <TouchableOpacity
// //                     onPress={() => {
// //                       try {
// //                         if (Platform.OS === "web") {
// //                           window.open(`${PDF_BASE_URL}/${result.pdf_path}`, "_blank");
// //                         } else {
// //                           Linking.openURL(`${PDF_BASE_URL}/${result.pdf_path}`);
// //                         }
// //                       } catch (err) {
// //                         console.error("Failed to open PDF:", err);
// //                         Alert.alert("Error", "Could not open PDF file.");
// //                       }
// //                     }}
// //                     style={{ marginTop: 8 }}
// //                   >
// //                     <Text style={{ color: "#007BFF" }}>📄 View Submitted PDF</Text>
// //                   </TouchableOpacity>
// //                 )}
// //                 {result.graded_pdf_path && (
// //                   <TouchableOpacity
// //                     onPress={() => {
// //                       if (Platform.OS === "web") {
// //                         window.open(`${PDF_BASE_URL}/${result.graded_pdf_path}`, "_blank");
// //                       } else {
// //                         Linking.openURL(`${PDF_BASE_URL}/${result.graded_pdf_path}`);
// //                       }
// //                     }}
// //                     style={{ marginTop: 5 }}
// //                   >
// //                     <Text style={{ color: "#28a745" }}>📄 View Graded PDF</Text>
// //                   </TouchableOpacity>
// //                 )}
// //               </View>
// //             ))
// //           )}
// //         </ScrollView>
// //       )}
// //     </View>
// //   );
// // };

// // export default TeacherTestReport;



// // // import React, { useState, useEffect } from "react";
// // // import {
// // //   View,
// // //   Text,
// // //   ScrollView,
// // //   TouchableOpacity,
// // //   TextInput,
// // //   ActivityIndicator,
// // //   Alert,
// // //   Platform,
// // //   Linking,
// // // } from "react-native";
// // // import axios from "axios";
// // // import { FASTAPI_BASE_URL, PDF_BASE_URL } from "./config";

// // // const TeacherTestReport = ({ testId = null, isDarkMode, styles, handleSelectPastTestResultTeacher }) => {
// // //   const [results, setResults] = useState([]);
// // //   const [loadingResults, setLoadingResults] = useState(false);
// // //   const [gradingState, setGradingState] = useState({});

// // //   useEffect(() => {
// // //     const fetchResults = async () => {
// // //       if (!testId) return;
// // //       setLoadingResults(true);
// // //       try {
// // //         const res = await axios.get(`${FASTAPI_BASE_URL}/tests/${testId}/results`);
// // //         console.log("📥 Loaded test results:", res.data);
// // //         setResults(res.data);
// // //       } catch (error) {
// // //         console.error("❌ Error fetching test results:", error);
// // //       } finally {
// // //         setLoadingResults(false);
// // //       }
// // //     };
// // //     fetchResults();
// // //   }, [testId]);

// // //   const updateGrading = (qid, data) => {
// // //     setGradingState((prev) => ({
// // //       ...prev,
// // //       [qid]: { ...prev[qid], ...data },
// // //     }));
// // //   };

// // //   const submitGrades = async (studentTestId) => {
// // //     const payload = {
// // //       student_test_id: studentTestId,
// // //       graded_answers: Object.entries(gradingState).map(([test_question_id, data]) => ({
// // //         test_question_id: parseInt(test_question_id),
// // //         teacher_score: parseInt(data.teacher_score) || 0,
// // //         teacher_comment: data.teacher_comment || "",
// // //       })),
// // //     };
// // //     console.log("📤 Submitting grades:", payload);
// // //     try {
// // //       await axios.post(`${FASTAPI_BASE_URL}/teacher/grade-student-test`, payload);
// // //       Alert.alert("✅ Success", "Grades saved.");
// // //       setGradingState({});
// // //     } catch (err) {
// // //       console.error("❌ Grading error:", err);
// // //       Alert.alert("❌ Error", "Failed to save grades.");
// // //     }
// // //   };

// // //   return (
// // //     <View style={{ marginTop: 20 }}>
// // //       <Text style={styles(isDarkMode).sectionTitle}>
// // //         Test Results (Test ID: {testId})
// // //       </Text>
// // //       {loadingResults ? (
// // //         <ActivityIndicator size="large" color="#007BFF" />
// // //       ) : (
// // //         <ScrollView
// // //           style={{
// // //             maxHeight: 300,
// // //             borderWidth: 1,
// // //             borderColor: "#ccc",
// // //             padding: 10,
// // //             marginTop: 10,
// // //           }}
// // //         >
// // //           {results.length === 0 ? (
// // //             <Text>No results available.</Text>
// // //           ) : (
// // //             results.map((result) => (
// // //               <View
// // //                 key={result.id}
// // //                 style={{
// // //                   marginBottom: 10,
// // //                   padding: 10,
// // //                   backgroundColor: isDarkMode ? "#333" : "#f9f9f9",
// // //                   borderRadius: 5,
// // //                 }}
// // //               >
// // //                 <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
// // //                   Student: {result.student_email}
// // //                 </Text>
// // //                 <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
// // //                   Score: {result.score} / {result.total_score}
// // //                 </Text>

// // //                 {result.answers &&
// // //                   Object.entries(result.answers).map(([qid, ans]) => (
// // //                     <View key={qid} style={{ marginTop: 8 }}>
// // //                       <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
// // //                         Q{qid}: {JSON.stringify(ans)}
// // //                       </Text>
// // //                       <TextInput
// // //                         placeholder="Teacher Comment"
// // //                         value={gradingState[qid]?.teacher_comment || ""}
// // //                         onChangeText={(text) => updateGrading(qid, { teacher_comment: text })}
// // //                         style={{
// // //                           borderWidth: 1,
// // //                           borderColor: "#aaa",
// // //                           padding: 5,
// // //                           marginVertical: 5,
// // //                           backgroundColor: "#fff",
// // //                         }}
// // //                       />
// // //                       <TextInput
// // //                         placeholder="Score"
// // //                         keyboardType="numeric"
// // //                         value={
// // //                           gradingState[qid]?.teacher_score !== undefined
// // //                             ? gradingState[qid].teacher_score.toString()
// // //                             : ""
// // //                         }
// // //                         onChangeText={(text) =>
// // //                           updateGrading(qid, {
// // //                             teacher_score: parseInt(text, 10) || 0,
// // //                           })
// // //                         }
// // //                         style={{
// // //                           borderWidth: 1,
// // //                           borderColor: "#aaa",
// // //                           padding: 5,
// // //                           backgroundColor: "#fff",
// // //                         }}
// // //                       />
// // //                     </View>
// // //                   ))}

// // //                 {/* Submit Button */}
// // //                 <TouchableOpacity
// // //                   onPress={() => submitGrades(result.id)}
// // //                   style={{
// // //                     marginTop: 10,
// // //                     backgroundColor: "#007BFF",
// // //                     padding: 8,
// // //                     borderRadius: 4,
// // //                   }}
// // //                 >
// // //                   <Text style={{ color: "#fff", textAlign: "center" }}>
// // //                     💾 Submit Grades
// // //                   </Text>
// // //                 </TouchableOpacity>

// // //                 {/* PDF Link */}
// // //                 {result.pdf_path && (
// // //                   <TouchableOpacity
// // //                     onPress={() => {
// // //                       try {
// // //                         const pdfURL = `${PDF_BASE_URL}/${result.pdf_path}`;
// // //                         if (Platform.OS === "web") {
// // //                           window.open(pdfURL, "_blank");
// // //                         } else {
// // //                           Linking.openURL(pdfURL);
// // //                         }
// // //                       } catch (err) {
// // //                         console.error("Failed to open PDF:", err);
// // //                         Alert.alert("Error", "Could not open PDF file.");
// // //                       }
// // //                     }}
// // //                     style={{ marginTop: 8 }}
// // //                   >
// // //                     <Text style={{ color: "#007BFF" }}>📄 View Submitted PDF</Text>
// // //                   </TouchableOpacity>
// // //                 )}
// // //               </View>
// // //             ))
// // //           )}
// // //         </ScrollView>
// // //       )}
// // //     </View>
// // //   );
// // // };

// // // export default TeacherTestReport;




// // // import React, { useState, useEffect } from "react";
// // // // import { View, Text, TextInput, ScrollView, ActivityIndicator } from "react-native";
// // // import axios from "axios";
// // // import { FASTAPI_BASE_URL, PDF_BASE_URL } from "./config"; // adjust path if necessary
// // // import { TouchableOpacity, Text, TextInput, View, ScrollView, ActivityIndicator, Alert } from "react-native";

// // // const TeacherTestReport = ({ testId = null, isDarkMode, styles, handleSelectPastTestResultTeacher  }) => {
// // //   const [results, setResults] = useState([]);
// // //   const [loadingResults, setLoadingResults] = useState(false);
// // //   const [gradingState, setGradingState] = useState({});

// // //   useEffect(() => {
// // //     const fetchResults = async () => {
// // //       if (!testId) return;
// // //       setLoadingResults(true);
// // //       try {
// // //         const res = await axios.get(
// // //           `${FASTAPI_BASE_URL}/tests/${testId}/results`
// // //         );
// // //         setResults(res.data);
// // //       } catch (error) {
// // //         console.error("Error fetching test results:", error);
// // //       } finally {
// // //         setLoadingResults(false);
// // //       }
// // //     };
// // //     fetchResults();
// // //   }, [testId]);
  
// // //   const updateGrading = (qid, data) => {
// // //   setGradingState((prev) => ({
// // //     ...prev,
// // //     [qid]: { ...prev[qid], ...data },
// // //   }));
// // // };

// // // const submitGrades = async (studentTestId) => {
// // //   const payload = {
// // //     student_test_id: studentTestId,
// // //     graded_answers: Object.entries(gradingState).map(([test_question_id, data]) => ({
// // //       test_question_id: parseInt(test_question_id),
// // //       teacher_score: parseInt(data.teacher_score) || 0,
// // //       teacher_comment: data.teacher_comment || ""
// // //     })),
// // //   };

// // //   try {
// // //     await axios.post(`${FASTAPI_BASE_URL}/teacher/grade-student-test`, payload);
// // //     Alert.alert("Success", "Grades saved.");
// // //   } catch (err) {
// // //     console.error("Grading error:", err);
// // //     Alert.alert("Error", "Failed to save grades.");
// // //   }
// // // };

   
// // // return (
// // //   <View style={{ marginTop: 20 }}>
// // //     <Text style={styles(isDarkMode).sectionTitle}>
// // //       Test Results (Test ID: {testId})
// // //     </Text>
// // //     {loadingResults ? (
// // //       <ActivityIndicator size="large" color="#007BFF" />
// // //     ) : (
// // //       <ScrollView
// // //         style={{
// // //           maxHeight: 300,
// // //           borderWidth: 1,
// // //           borderColor: "#ccc",
// // //           padding: 10,
// // //           marginTop: 10,
// // //         }}
// // //       >
// // //         {results.length === 0 ? (
// // //           <Text>No results available.</Text>
// // //         ) : (
// // //           results.map((result) => (
// // //             <View
// // //               key={result.id}
// // //               style={{
// // //                 marginBottom: 10,
// // //                 padding: 10,
// // //                 backgroundColor: isDarkMode ? "#333" : "#f9f9f9",
// // //                 borderRadius: 5,
// // //               }}
// // //             >
// // //               <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
// // //                 Student: {result.student_email}
// // //               </Text>
// // //               <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
// // //                 Score: {result.score} / {result.total_score}
// // //               </Text>

// // //               {result.answers &&
// // //                 Object.entries(result.answers).map(([qid, ans]) => (
// // //                   <View key={qid} style={{ marginTop: 8 }}>
// // //                     <Text style={{ color: isDarkMode ? "#fff" : "#000" }}>
// // //                       Q{qid}: {JSON.stringify(ans)}
// // //                     </Text>
// // //                     <TextInput
// // //                       placeholder="Teacher Comment"
// // //                       value={gradingState[qid]?.teacher_comment || ""}
// // //                       onChangeText={(text) =>
// // //                         updateGrading(qid, { teacher_comment: text })
// // //                       }
// // //                       style={{
// // //                         borderWidth: 1,
// // //                         borderColor: "#aaa",
// // //                         padding: 5,
// // //                         marginVertical: 5,
// // //                         backgroundColor: "#fff",
// // //                       }}
// // //                     />
// // //                     <TextInput
// // //                       placeholder="Score"
// // //                       keyboardType="numeric"
// // //                       value={
// // //                         gradingState[qid]?.teacher_score !== undefined
// // //                           ? gradingState[qid]?.teacher_score.toString()
// // //                           : ""
// // //                       }
// // //                       onChangeText={(text) =>
// // //                         updateGrading(qid, {
// // //                           teacher_score: parseInt(text, 10) || 0,
// // //                         })
// // //                       }
// // //                       style={{
// // //                         borderWidth: 1,
// // //                         borderColor: "#aaa",
// // //                         padding: 5,
// // //                         backgroundColor: "#fff",
// // //                       }}
// // //                     />
// // //                   </View>
// // //                 ))}

// // //               {/* Submit Button */}
// // //               <TouchableOpacity
// // //                 onPress={() => submitGrades(result.id)}
// // //                 style={{
// // //                   marginTop: 10,
// // //                   backgroundColor: "#007BFF",
// // //                   padding: 8,
// // //                   borderRadius: 4,
// // //                 }}
// // //               >
// // //                 <Text style={{ color: "#fff", textAlign: "center" }}>
// // //                   💾 Submit Grades
// // //                 </Text>
// // //               </TouchableOpacity>

// // //               {/* PDF Link */}
// // //               {result.pdf_path && (
// // //                 <TouchableOpacity
// // //                   onPress={() => {
// // //                     try {
// // //                       if (Platform.OS === "web") {
// // //                         window.open(
// // //                           `${PDF_BASE_URL}/${result.pdf_path}`,
// // //                           "_blank"
// // //                         );
// // //                       } else {
// // //                         Linking.openURL(`${PDF_BASE_URL}/${result.pdf_path}`);
// // //                       }
// // //                     } catch (err) {
// // //                       console.error("Failed to open PDF:", err);
// // //                       Alert.alert("Error", "Could not open PDF file.");
// // //                     }
// // //                   }}
// // //                   style={{ marginTop: 8 }}
// // //                 >
// // //                   <Text style={{ color: "#007BFF" }}>
// // //                     📄 View Submitted PDF
// // //                   </Text>
// // //                 </TouchableOpacity>
// // //               )}
// // //             </View>
// // //           ))
// // //         )}
// // //       </ScrollView>
// // //     )}
// // //   </View>
// // // );

// // // };

// // // export default TeacherTestReport;




